/**
 * RedactionPlanProgress — Carte de progression d'un job de rédaction.
 *
 * Replicate visuel **strict** du dialog `ResearchProgressDisplay` de l'add-in
 * Word (`apps/addin-word/src/taskpane/components/hector-ai/ResearchProgressDisplay.tsx`)
 * + `PhaseIndicator.tsx` + `ArgumentsCollapsible.tsx` + `QueriesCollapsible.tsx`.
 * Validé @user 2026-06-19.
 *
 * Différences inhérentes au contexte web (pas un modal full-screen, vit dans
 * la timeline chat) :
 *  - Pas de backdrop blur / overlay
 *  - Largeur naturelle de la timeline (pas de `max-w-sm` du modal Word)
 *
 * La barre de progression est auto-incrémentée vers un plafond dépendant de
 * la phase serveur — signal visuel constant pendant les périodes silencieuses
 * (recherche LLM, attente Mistral).
 */

import * as React from 'react';
import { Loader2, CheckCircle, AlertCircle, RotateCcw, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SearchQuery, LegalVerificationState } from '@/features/redaction/types/research-progress';
import { useRedactionThinkingStore } from '@/features/redaction/stores/redactionThinkingStore';
import {
  lireAvancementServi,
  noterAvancementServi,
} from '@/features/redaction/streaming/redactionProgressLedger';
import { RedactionArgumentsCollapsible } from './RedactionArgumentsCollapsible';
import { RedactionQueriesCollapsible } from './RedactionQueriesCollapsible';
import { RedactionThinkingStream } from './RedactionThinkingStream';
import { productionRedigee, type ProductionRedigee } from './productionRedigee';

type Phase = 'planning' | 'research' | 'aggregation' | 'preparation' | 'insertion' | 'complete' | 'error';

interface RedactionPlanProgressProps {
  requestId: string;
  jobId: string;
  kind: 'research' | 'generation';
  phase: Phase;
  argumentTitles?: string[];
  completedQueries?: number;
  totalQueries?: number;
  /** Queries détaillées populées par les events worker (research-only). */
  searchQueries?: SearchQuery[];
  /** État vérification Légifrance des fondements (research-only). */
  legalVerification?: LegalVerificationState;
  /** Toggle JP désactivée (research-only) : 2 phases, sans la recherche JP. */
  skipJurisprudence?: boolean;
  /**
   * Lancement en un seul temps : le job produit l'acte, pas un plan. Mêmes étapes, mais
   * la dernière se nomme « Rédaction de l'acte » — annoncer « Insertion » ferait attendre
   * un plan à valider qui ne viendra pas.
   */
  fullDocument?: boolean;
  errorMessage?: string;
  /** Dernière étape atteinte avant l'échec — situe la panne dans la phrase d'erreur. */
  errorPhase?: Phase;
  /**
   * Optionnel — affiche « Relancer … » sous une erreur.
   *
   * Renvoie le DERNIER message de l'avocat (même canal que le « Réessayer » des
   * erreurs agent), ce qui fait re-proposer la carte de lancement. On ne rejoue
   * PAS le `requestId` d'origine : une fois le job échoué le backend n'attend
   * plus cette requête, donc la rejouer ne produirait rien. Absent sur les
   * échanges d'historique, où relancer n'a pas de sens.
   */
  onRetry?: () => void;
  proposedPayload: {
    nom_acte?: string | null;
    generation_mode?: 'full' | 'section_i' | 'section_ii_iii';
  };
  /**
   * Optionnel — affiche le bouton « Annuler la génération » en bas si fourni.
   * Câblage in-flight (post-enqueue) à brancher en follow-up :
   * `RedactionJobCollector` n'écoute pas un signal d'annulation après le clic
   * Lancer, donc tant que ce n'est pas implémenté côté back, on laisse le
   * bouton masqué (`onCancel` undef) plutôt que d'afficher un bouton inerte.
   */
  onCancel?: () => void;
  /**
   * Mode « déplié dans le bandeau results » : sans carte propre (bordure/marge)
   * ni header titre — le bandeau `RedactionPlanResults` porte déjà le titre.
   * Utilisé pour re-consulter le détail (arguments/JP/synthèse) après le
   * terminal (2026-07-13).
   */
  embedded?: boolean;
}

// « Préparation » et « Insertion » sont deux pastilles : entre la fin des recherches et le
// premier mot écrit dans le canvas, le worker compose le plan pendant plusieurs minutes.
// Annoncer « Insertion » dès la fin de la synthèse faisait attendre un texte qui n'arrivait pas.
const RESEARCH_PHASES: Array<{ id: Phase; label: string }> = [
  { id: 'planning', label: 'Arguments' },
  { id: 'research', label: 'Recherche JP' },
  { id: 'aggregation', label: 'Synthèse' },
  { id: 'preparation', label: 'Préparation' },
  { id: 'insertion', label: 'Insertion' },
];
const RESEARCH_PHASES_NO_JP: Array<{ id: Phase; label: string }> = [
  { id: 'planning', label: 'Arguments' },
  { id: 'preparation', label: 'Préparation' },
  { id: 'insertion', label: 'Insertion' },
];
/**
 * Lancement en un seul temps : mêmes étapes que la recherche — le job émet les mêmes
 * événements, la recherche et l'agrégation ont bien lieu — mais la dernière n'insère pas
 * un plan, elle rédige l'acte. Seul le libellé final change.
 */
const FULL_DOCUMENT_PHASES: Array<{ id: Phase; label: string }> = [
  { id: 'planning', label: 'Arguments' },
  { id: 'research', label: 'Recherche JP' },
  { id: 'aggregation', label: 'Synthèse' },
  { id: 'preparation', label: 'Préparation' },
  { id: 'insertion', label: "Rédaction de l'acte" },
];
const FULL_DOCUMENT_PHASES_NO_JP: Array<{ id: Phase; label: string }> = [
  { id: 'planning', label: 'Arguments' },
  { id: 'preparation', label: 'Préparation' },
  { id: 'insertion', label: "Rédaction de l'acte" },
];
const GENERATION_PHASES: Array<{ id: Phase; label: string }> = [
  { id: 'planning', label: 'Rédaction' },
  { id: 'insertion', label: 'Insertion' },
];

const PHASE_ORDER: Phase[] = ['planning', 'research', 'aggregation', 'preparation', 'insertion', 'complete'];

function getProgressCap(kind: 'research' | 'generation', phase: Phase, hasTitles: boolean, skipJp: boolean): number {
  if (phase === 'complete') return 100;
  if (phase === 'error') return 0;
  if (kind === 'research') {
    if (skipJp) {
      switch (phase) {
        case 'planning': return hasTitles ? 50 : 20;
        // Ces deux phases n'ont PAS de pastille quand la recherche JP est coupée. Elles ne
        // devraient plus survenir (le reducer ne les pose plus), mais un plafond à 0 fige
        // la barre au lieu de la ralentir : une phase inattendue ne doit jamais valoir un
        // écran mort. On les traite donc comme la fin de « Arguments ».
        case 'research':
        case 'aggregation': return hasTitles ? 50 : 20;
        case 'preparation': return 85;
        case 'insertion': return 95;
        default: return 0;
      }
    }
    switch (phase) {
      case 'planning': return hasTitles ? 20 : 10;
      case 'research': return 70;
      case 'aggregation': return 85;
      case 'preparation': return 90;
      case 'insertion': return 95;
      default: return 0;
    }
  }
  switch (phase) {
    case 'planning': return 60;
    case 'insertion': return 95;
    default: return 0;
  }
}

function getTitle(production: ProductionRedigee): string {
  return production === 'plan' ? 'Génération du plan détaillé' : 'Génération de l\'acte';
}

/** Ce que l'avocat attendait — sujet de la phrase d'erreur. */
function getWorkLabel(production: ProductionRedigee): string {
  return production === 'plan' ? 'la génération du plan détaillé' : "la génération de l'acte";
}

/**
 * Phrase d'erreur SITUÉE, en français d'avocat.
 *
 * L'ancien libellé — « Erreur du job » — n'apprenait rien : ni ce qui avait
 * échoué, ni à quel moment. Une panne n'arrive jamais « au job », elle arrive
 * pendant une étape identifiable, et c'est cette étape qui dit à l'avocat si son
 * travail est perdu ou seulement incomplet (2026-08-09).
 */
function getErrorHeadline(
  kind: 'research' | 'generation',
  production: ProductionRedigee,
  errorPhase?: Phase,
): string {
  // Le SUJET de la phrase est le produit (« la génération de l'acte »), mais la
  // branche `planning` reste indexée sur `kind` : elle ne parle pas du produit,
  // elle nomme une étape — l'identification des arguments — qui a bien lieu
  // dans les deux déroulés de recherche, y compris en un seul temps.
  const work = getWorkLabel(production);
  switch (errorPhase) {
    case 'planning':
      return kind === 'research'
        ? "Une erreur est survenue pendant l'identification des arguments."
        : `Une erreur est survenue au démarrage de ${work}.`;
    case 'research':
      return 'Une erreur est survenue pendant la recherche de jurisprudence.';
    case 'aggregation':
      return 'Une erreur est survenue pendant la synthèse des recherches.';
    case 'preparation':
      return production === 'plan'
        ? 'Une erreur est survenue pendant la préparation du plan détaillé.'
        : "Une erreur est survenue pendant la préparation de l'acte.";
    case 'insertion':
      return "Une erreur est survenue pendant l'insertion dans le document.";
    default:
      return `Une erreur est survenue pendant ${work}.`;
  }
}

/**
 * PhaseIndicator — pills horizontales avec connecteurs (port direct de
 * `apps/addin-word/.../PhaseIndicator.tsx`).
 */
function PhaseIndicator({ phases, currentPhase }: { phases: Array<{ id: Phase; label: string }>; currentPhase: Phase }) {
  const currentIdx = PHASE_ORDER.indexOf(currentPhase);
  return (
    <div className="flex items-center justify-center gap-1 flex-wrap">
      {phases.map((phase, index) => {
        const pIdx = PHASE_ORDER.indexOf(phase.id);
        const status: 'pending' | 'active' | 'complete' =
          currentPhase === 'complete' || pIdx < currentIdx ? 'complete'
          : pIdx === currentIdx ? 'active'
          : 'pending';
        return (
          <React.Fragment key={phase.id}>
            <span
              // L'étape en cours n'était signalée que par la couleur : invisible
              // pour un lecteur d'écran, et inobservable autrement qu'en visant
              // une classe CSS (#865). `aria-current="step"` est la façon
              // standard de désigner l'étape courante d'une progression.
              aria-current={status === 'active' ? 'step' : undefined}
              className={cn(
                'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-colors whitespace-nowrap',
                status === 'active' && 'bg-white text-stone-900 border-stone-900 font-medium',
                status === 'complete' && 'bg-stone-900 text-white border-stone-900',
                status === 'pending' && 'bg-stone-50 text-stone-400 border-stone-200',
              )}
            >
              {status === 'active' && <Loader2 className="w-2.5 h-2.5 animate-spin shrink-0" />}
              {phase.label}
            </span>
            {index < phases.length - 1 && (
              <div
                className={cn(
                  'h-px w-2 shrink-0',
                  status === 'complete' ? 'bg-stone-400' : 'bg-stone-200',
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * StatusBox — boîte bordée avec icône (spinner ou check) et texte. Port du
 * pattern Word `ResearchProgressDisplay`.
 */
function StatusBox({ active, text }: { active: boolean; text: string }) {
  return (
    <div className="border border-stone-100 rounded-md p-2.5 flex items-center gap-2">
      {active ? (
        <Loader2 className="w-3.5 h-3.5 text-stone-400 animate-spin shrink-0" />
      ) : (
        <CheckCircle className="w-3.5 h-3.5 text-stone-300 shrink-0" />
      )}
      <span className="text-xs text-stone-600">{text}</span>
    </div>
  );
}

export function RedactionPlanProgress({
  jobId,
  kind,
  phase,
  argumentTitles = [],
  completedQueries = 0,
  totalQueries = 0,
  searchQueries = [],
  legalVerification,
  skipJurisprudence = false,
  fullDocument = false,
  errorMessage,
  errorPhase,
  onRetry,
  proposedPayload,
  onCancel,
  embedded = false,
}: RedactionPlanProgressProps) {
  // Reasoning du modèle pendant le stream SSE canvas (frames `thinking`) —
  // alimenté par `useRedactionStreamingState`, donc pour le travail affiché par
  // le canvas (le relais hors React de #1152 n'a jamais tourné ; retiré, #1329).
  // Le store est indexé par jobId : seule la carte du job concerné l'affiche
  // (les cartes historiques de la timeline restent muettes), et deux rédactions
  // simultanées ne se volent plus leur raisonnement. Pendant les phases amont
  // (planning/recherche/synthèse) le stream n'a pas commencé → content vide.
  const thinkingContent = useRedactionThinkingStore((s) => s.parJob[jobId]?.content ?? '');
  const thinkingActive = useRedactionThinkingStore(
    (s) => s.parJob[jobId] !== undefined && !s.parJob[jobId].hasContentStarted,
  );
  const hasTitles = argumentTitles.length > 0;
  const phases = kind !== 'research'
    ? GENERATION_PHASES
    : fullDocument
      ? (skipJurisprudence ? FULL_DOCUMENT_PHASES_NO_JP : FULL_DOCUMENT_PHASES)
      : (skipJurisprudence ? RESEARCH_PHASES_NO_JP : RESEARCH_PHASES);
  const cap = getProgressCap(kind, phase, hasTitles, skipJurisprudence);
  // Ce que le job PRODUIT, pas quel job tourne : en un seul temps, `kind` vaut
  // encore 'research' alors que le canvas reçoit l'acte (cf. `productionRedigee`).
  const production = productionRedigee(kind, fullDocument);
  const title = getTitle(production);

  // La rampe repart d'où elle en était, pas de zéro : quitter la conversation
  // démonte cette carte, et la valeur ne survit que dans le registre hors React.
  // Sans lui, revenir sur une rédaction en cours la montrait à 0 % — un travail
  // presque fini qu'on croit à peine commencé (#1152).
  const [overall, setOverall] = React.useState(() => lireAvancementServi(jobId));
  // Un changement de `jobId` sans démontage (la carte est clée sur l'item de la
  // frise, pas sur le travail) doit resynchroniser : sinon la carte du nouveau
  // travail hériterait de l'avancement de l'ancien.
  const jobPrecedent = React.useRef(jobId);
  if (jobPrecedent.current !== jobId) {
    jobPrecedent.current = jobId;
    setOverall(lireAvancementServi(jobId));
  }
  React.useEffect(() => {
    if (phase === 'complete') { setOverall(100); return undefined; }
    if (phase === 'error') return undefined;
    const id = setInterval(() => {
      setOverall((prev) => (prev >= cap ? prev : Math.min(prev + 1, cap)));
    }, 800);
    return () => clearInterval(id);
  }, [cap, phase]);
  // L'écriture est un effet, pas un geste glissé dans le calculateur d'état
  // ci-dessus : React peut rejouer un calculateur (mode strict), et un registre
  // écrit deux fois par tour serait un piège pour le prochain lecteur.
  React.useEffect(() => {
    noterAvancementServi(jobId, overall);
  }, [jobId, overall]);

  // Erreur — short variant, parité Word.
  if (phase === 'error') {
    return (
      <div className="my-3 rounded-lg border border-stone-200 bg-white p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-stone-900">
              {getErrorHeadline(kind, production, errorPhase)}
            </p>
            {/* Détail technique du backend, en retrait : utile pour comprendre,
                jamais la première chose lue. */}
            {errorMessage && <p className="text-xs text-stone-400 mt-1">{errorMessage}</p>}
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-stone-200 px-2.5 py-1 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-50"
              >
                <RotateCcw className="h-3.5 w-3.5 text-stone-500" aria-hidden />
                Relancer {getWorkLabel(production)}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // « Arguments » s'ouvre quand : planning a produit des titres OU vérification
  // légale en cours (parité Word).
  const isArgumentsOpen =
    (phase === 'planning' && hasTitles) ||
    legalVerification?.status === 'in_progress';

  const shouldShowQueries =
    !skipJurisprudence && (
      phase === 'research' ||
      phase === 'aggregation' ||
      phase === 'preparation' ||
      phase === 'insertion' ||
      phase === 'complete'
    );

  return (
    <div className={embedded ? undefined : 'my-3 rounded-lg border border-stone-200 bg-white overflow-hidden'}>
      <div className="p-4 space-y-4">
        {/* Header — masqué en mode embedded (le bandeau results porte le titre) */}
        {!embedded && (
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-stone-900">
              {title}
              {proposedPayload.nom_acte ? <span className="text-stone-500 font-normal"> · {proposedPayload.nom_acte}</span> : null}
            </h3>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="p-1 rounded hover:bg-stone-100 transition-colors"
                aria-label="Fermer"
              >
                <X className="w-3.5 h-3.5 text-stone-400" />
              </button>
            )}
          </div>
        )}

        {/* Barre de progression */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-[3px] bg-stone-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-stone-900 rounded-full"
              style={{ width: `${Math.min(100, overall)}%`, transition: 'width 0.4s ease' }}
            />
          </div>
          <span className="text-xs text-stone-500 tabular-nums shrink-0">{Math.round(overall)}%</span>
        </div>

        {/* Pills horizontales */}
        <PhaseIndicator phases={phases} currentPhase={phase} />

        {/* Planning : « Construction des arguments... » UNIQUEMENT tant que pas
            de titres reçus. Dès qu'on a `argumentTitles`, l'`ArgumentsCollapsible`
            prend le relais. */}
        {kind === 'research' && phase === 'planning' && !hasTitles && (
          <StatusBox active text="Construction des arguments juridiques en cours…" />
        )}

        {/* Arguments collapsible — apparaît dès que des titres arrivent */}
        {kind === 'research' && hasTitles && (
          <RedactionArgumentsCollapsible
            titles={argumentTitles}
            isOpen={isArgumentsOpen}
            legalVerification={legalVerification}
          />
        )}

        {/* Queries (recherche JP) — collapsible par argument avec circulaires */}
        {kind === 'research' && shouldShowQueries && searchQueries.length > 0 && (
          <RedactionQueriesCollapsible
            queries={searchQueries}
            completedCount={completedQueries}
            totalCount={totalQueries || searchQueries.length}
            currentPhase={phase}
          />
        )}

        {/* Aggregation — boîte d'activité */}
        {kind === 'research' && !skipJurisprudence && (phase === 'aggregation' || phase === 'preparation' || phase === 'insertion' || phase === 'complete') && (
          <StatusBox
            active={phase === 'aggregation'}
            text={
              phase === 'aggregation'
                ? 'Synthétisation des résultats et sélection des meilleures JP…'
                : 'Synthétisation des résultats et sélection des meilleures JP'
            }
          />
        )}

        {/* Préparation — le worker compose le plan (ou l'acte) : rien ne s'écrit encore
            dans le canvas, et cette étape peut durer plusieurs minutes. */}
        {kind === 'research' && (phase === 'preparation' || phase === 'insertion' || phase === 'complete') && (
          <StatusBox
            active={phase === 'preparation'}
            text={
              production === 'plan'
                ? (phase === 'preparation' ? 'Préparation du plan détaillé…' : 'Plan détaillé préparé')
                : (phase === 'preparation' ? "Préparation de l'acte…" : 'Acte préparé')
            }
          />
        )}

        {/* Insertion — en un seul temps, cette étape n'insère pas un plan : elle
            rédige l'acte. Dire « Insertion du plan » y ferait attendre un plan à
            valider qui ne viendra pas, exactement comme la pastille d'étape que
            `FULL_DOCUMENT_PHASES` nomme déjà « Rédaction de l'acte ». */}
        {kind === 'research' && (phase === 'insertion' || phase === 'complete') && (
          <StatusBox
            active={phase === 'insertion'}
            text={
              production === 'plan'
                ? (phase === 'insertion' ? 'Insertion du plan dans le canvas…' : 'Plan inséré dans le canvas.')
                : (phase === 'insertion' ? 'Rédaction de l\'acte dans le canvas…' : 'Acte inséré dans le canvas.')
            }
          />
        )}

        {/* Generation */}
        {kind === 'generation' && phase === 'planning' && (
          <StatusBox active text="Rédaction de l'acte en cours…" />
        )}
        {kind === 'generation' && phase === 'insertion' && (
          <StatusBox active text="Insertion de l'acte dans le canvas…" />
        )}
        {kind === 'generation' && phase === 'complete' && (
          <StatusBox active={false} text="Acte inséré dans le canvas." />
        )}

        {/* Reasoning du modèle streamé pendant l'insertion (parité add-in
            Word : ResearchProgressDisplay → ThinkingStreamDisplay). Indenté
            sous la StatusBox d'insertion active. Masqué en terminal — comme
            dans Word où le dialog se ferme après complete. (phase 'error' est
            déjà éliminée par l'early-return du variant erreur plus haut.) */}
        {thinkingContent && phase !== 'complete' && (
          <div className="ml-6">
            <RedactionThinkingStream content={thinkingContent} isThinking={thinkingActive} />
          </div>
        )}
      </div>

      {/* Bouton « Annuler la génération » fixé en bas — parité Word */}
      {onCancel && (
        <div className="border-t border-stone-100 p-3">
          <button
            type="button"
            onClick={onCancel}
            className="w-full text-xs text-stone-400 hover:text-stone-600 transition-colors py-1.5"
          >
            Annuler la génération
          </button>
        </div>
      )}
    </div>
  );
}
